import { z } from "zod";
import { MAX_AUTHORS, MAX_NAME_LENGTH, MAX_TAGS, MAX_TITLE_LENGTH } from "./catalog";
import { METADATA_PROVIDER_IDS, type MetadataProviderId } from "./settings";
import { isIsbn10Valid, isIsbn13Valid, isValidPublishedDate } from "./text";

/*
 * Metadata lookup contract (add-metadata-enrichment): DTOs, request schemas, provider reference
 * formats and the pure normalizers used by the providers.
 */

export const METADATA_MAX_CANDIDATES = 10;
export const METADATA_MAX_SUBJECTS = 15;

// --- provider references ----------------------------------------------------------

/** Open Library edition (`M:OL123M`) or work (`W:OL45W`) reference. */
export const OL_CANDIDATE_REF = /^[MW]:OL\d{1,12}[MW]$/;
export const OL_COVER_REF = /^\d{1,12}$/;
export const GOOGLE_VOLUME_REF = /^[A-Za-z0-9_-]{8,16}$/;

/** DNB record id (`001`): 8 to 10 digits, the last one may be an `X` check character. */
export const DNB_CANDIDATE_REF = /^\d{8,10}[\dX]$/;
/** The DNB cover service is addressed by ISBN-13. */
export const DNB_COVER_REF = /^97[89]\d{10}$/;

export function isValidCandidateRef(provider: MetadataProviderId, ref: string): boolean {
  if (provider === "openlibrary") {
    return OL_CANDIDATE_REF.test(ref) && ref[0] === ref[ref.length - 1];
  }
  if (provider === "dnb") return DNB_CANDIDATE_REF.test(ref);
  return GOOGLE_VOLUME_REF.test(ref);
}

export function isValidCoverRef(provider: MetadataProviderId, ref: string): boolean {
  if (provider === "openlibrary") return OL_COVER_REF.test(ref);
  if (provider === "dnb") return DNB_COVER_REF.test(ref);
  return GOOGLE_VOLUME_REF.test(ref);
}

export function isMetadataProvider(value: string): value is MetadataProviderId {
  return (METADATA_PROVIDER_IDS as readonly string[]).includes(value);
}

// --- DTOs ---------------------------------------------------------------------------

export const PROVIDER_STATUSES = ["ok", "no_results", "timeout", "error", "disabled"] as const;
export type ProviderStatus = (typeof PROVIDER_STATUSES)[number];

export interface MetadataCandidateSummaryDto {
  provider: MetadataProviderId;
  /** Opaque reference for the details endpoint. */
  ref: string;
  title: string;
  subtitle: string | null;
  authors: string[];
  publishedDate: string | null;
  publisher: string | null;
  isbn13: string | null;
  language: string | null;
  hasCover: boolean;
  /** Server-side cover preview URL (never a provider host); `null` without cover. */
  coverUrl: string | null;
}

export interface MetadataProviderResultDto {
  provider: MetadataProviderId;
  status: ProviderStatus;
  /** Error code when `status` is `error` (`provider_error`, `provider_quota_exceeded`). */
  code?: string;
}

export interface MetadataSearchDto {
  candidates: MetadataCandidateSummaryDto[];
  providers: MetadataProviderResultDto[];
}

export interface MetadataCandidateDto {
  provider: MetadataProviderId;
  ref: string;
  title: string;
  subtitle: string | null;
  authors: string[];
  /** Sanitized HTML. */
  description: string | null;
  publisher: string | null;
  publishedDate: string | null;
  language: string | null;
  /** Tag suggestions, at most 15. */
  subjects: string[];
  identifiers: { type: string; value: string }[];
  hasCover: boolean;
  coverUrl: string | null;
}

// --- requests -----------------------------------------------------------------------

export const metadataSearchSchema = z.object({
  isbn: z.string({ error: "invalid" }).max(40, { error: "too_long" }).optional(),
  title: z.string({ error: "invalid" }).max(1000, { error: "too_long" }).optional(),
  author: z.string({ error: "invalid" }).max(1000, { error: "too_long" }).optional(),
});
export type MetadataSearchInput = z.infer<typeof metadataSearchSchema>;

export const METADATA_TITLE_MIN = 2;
export const METADATA_TITLE_MAX = 300;

const applyText = z.string({ error: "invalid" }).max(MAX_TITLE_LENGTH, { error: "too_long" });

export const metadataApplySchema = z.object({
  /** The book's `updatedAt` as last seen by the editor. */
  updatedAt: z.iso.datetime({ error: "invalid" }),
  /** Fields to apply; omitted fields stay unchanged. */
  fields: z
    .object({
      title: applyText.optional(),
      subtitle: applyText.nullable().optional(),
      authors: z
        .array(z.string({ error: "invalid" }).max(MAX_NAME_LENGTH, { error: "too_long" }), {
          error: "invalid",
        })
        .max(MAX_AUTHORS, { error: "too_many" })
        .optional(),
      description: z.string({ error: "invalid" }).nullable().optional(),
      publisher: applyText.nullable().optional(),
      publishedDate: z
        .string({ error: "invalid" })
        .max(10, { error: "too_long" })
        .nullable()
        .optional(),
      language: z.string({ error: "invalid" }).max(35, { error: "too_long" }).nullable().optional(),
      /** Added to the book's tags, never removing any. */
      tags: z
        .array(z.string({ error: "invalid" }).max(MAX_NAME_LENGTH, { error: "too_long" }), {
          error: "invalid",
        })
        .max(MAX_TAGS, { error: "too_many" })
        .optional(),
      /** Added, or replacing the identifier of the same type. */
      identifiers: z
        .array(
          z.object({
            type: z.string({ error: "invalid" }).max(32, { error: "too_long" }),
            value: z.string({ error: "invalid" }).max(MAX_NAME_LENGTH, { error: "too_long" }),
          }),
          { error: "invalid" },
        )
        .max(20, { error: "too_many" })
        .optional(),
    })
    .default({}),
  cover: z
    .object({
      provider: z.enum(METADATA_PROVIDER_IDS, { error: "invalid" }),
      ref: z.string({ error: "invalid" }).max(40, { error: "invalid" }),
    })
    .optional(),
});
export type MetadataApplyInput = z.infer<typeof metadataApplySchema>;

// --- normalizers --------------------------------------------------------------------

/** Strips separators and validates; returns the ISBN-13 for a valid ISBN-10 or ISBN-13. */
export function toIsbn13(raw: string): string | undefined {
  const value = raw.replace(/[\s-]/g, "").toUpperCase();
  if (isIsbn13Valid(value)) return value;
  if (!isIsbn10Valid(value)) return undefined;
  const base = `978${value.slice(0, 9)}`;
  let sum = 0;
  for (let i = 0; i < 12; i++) sum += Number(base[i]) * (i % 2 === 0 ? 1 : 3);
  return `${base}${(10 - (sum % 10)) % 10}`;
}

/** Valid ISBN-10 digits (separators stripped), or undefined. */
export function toIsbn10(raw: string): string | undefined {
  const value = raw.replace(/[\s-]/g, "").toUpperCase();
  return isIsbn10Valid(value) ? value : undefined;
}

const MONTHS: Record<string, number> = {
  jan: 1,
  january: 1,
  feb: 2,
  february: 2,
  mar: 3,
  march: 3,
  apr: 4,
  april: 4,
  may: 5,
  jun: 6,
  june: 6,
  jul: 7,
  july: 7,
  aug: 8,
  august: 8,
  sep: 9,
  sept: 9,
  september: 9,
  oct: 10,
  october: 10,
  nov: 11,
  november: 11,
  dec: 12,
  december: 12,
};

function formatDate(year: number, month?: number, day?: number): string | undefined {
  if (!Number.isInteger(year) || year < 1000 || year > 9999) return undefined;
  let result = String(year);
  if (month !== undefined) result += `-${String(month).padStart(2, "0")}`;
  if (month !== undefined && day !== undefined) result += `-${String(day).padStart(2, "0")}`;
  return isValidPublishedDate(result) ? result : undefined;
}

/**
 * Converts a provider date to `YYYY`, `YYYY-MM` or `YYYY-MM-DD`, or undefined if it cannot be parsed:
 * ISO forms and English month-name forms ("May 2001", "September 21, 1937", "21 Sep 1937").
 */
export function parseProviderDate(raw: string | null | undefined): string | undefined {
  const value = raw
    ?.trim()
    .replace(/\s+/g, " ")
    .replace(/(\d)(st|nd|rd|th)\b/gi, "$1")
    .replace(/\.$/, "");
  if (!value) return undefined;
  const iso = /^(\d{4})(?:-(\d{1,2})(?:-(\d{1,2}))?)?$/.exec(value);
  if (iso) {
    return formatDate(
      Number(iso[1]),
      iso[2] === undefined ? undefined : Number(iso[2]),
      iso[3] === undefined ? undefined : Number(iso[3]),
    );
  }
  const month = (name: string | undefined) => MONTHS[(name ?? "").toLowerCase().replace(/\.$/, "")];
  // "September 21, 1937" / "Sep 21 1937"
  let match = /^([A-Za-z]{3,9}\.?) (\d{1,2}),? (\d{4})$/.exec(value);
  if (match) {
    const m = month(match[1]);
    return m ? formatDate(Number(match[3]), m, Number(match[2])) : undefined;
  }
  // "21 Sep 1937" / "21 September, 1937"
  match = /^(\d{1,2}) ([A-Za-z]{3,9}\.?),? (\d{4})$/.exec(value);
  if (match) {
    const m = month(match[2]);
    return m ? formatDate(Number(match[3]), m, Number(match[1])) : undefined;
  }
  // "May 2001" / "May, 2001"
  match = /^([A-Za-z]{3,9}\.?),? (\d{4})$/.exec(value);
  if (match) {
    const m = month(match[1]);
    return m ? formatDate(Number(match[2]), m) : undefined;
  }
  return undefined;
}

/** Trims, de-duplicates case-insensitively (first spelling wins) and limits the list. */
export function normalizeSubjects(
  subjects: readonly string[],
  limit = METADATA_MAX_SUBJECTS,
): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const subject of subjects) {
    const text = subject.replace(/\s+/g, " ").trim();
    if (!text || text.length > MAX_NAME_LENGTH) continue;
    const key = text.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(text);
    if (result.length >= limit) break;
  }
  return result;
}
